// Sprint 20.3 PR-9 — runners store.
//
// Hydrates from `GET /api/runners` (the auth-gated read endpoint that
// PR-8 added for the admin panel). Owns a per-id map so the
// `RunnerChip` component can look up "what runner is this agent on" in
// O(1) without re-fetching on every render.
//
// Refresh model: poll every `POLL_INTERVAL_MS` while the tab is
// visible. Spec §I.1 mentions a Socket.io heartbeat extension as the
// future ingestion path; until that lands, polling at 30 s is enough
// for the cap-% tooltip — the founder doesn't watch the chip
// continuously, and the table view in `SettingsPage` polls at 10 s
// anyway. Keeping the chip's refresh slow keeps network noise low.
//
// Polling is mounted at the application level (`HomePage`) rather
// than per-chip so a chat with N visible agents doesn't fire N
// fetches per tick.

import { create } from 'zustand';
import { fetchRunners, type RunnerSnapshot } from '../lib/api';

const POLL_INTERVAL_MS = 30_000;

export interface RunnersState {
  runnersById: Record<string, RunnerSnapshot>;
  loaded: boolean;
  error: string | null;

  // Fetch /api/runners and replace `runnersById`. Returns the fetched
  // list so callers (story seeders, tests) can chain.
  hydrate: () => Promise<RunnerSnapshot[]>;

  // Direct setter so stories + unit tests can seed without going
  // through the network. Mirrors the pattern other stores use
  // (projects.store `_setProjects`, settings.store `setState`).
  _setRunners: (next: RunnerSnapshot[]) => void;

  resetForTests: () => void;
}

export const useRunnersStore = create<RunnersState>((set) => ({
  runnersById: {},
  loaded: false,
  error: null,

  hydrate: async () => {
    try {
      const res = await fetchRunners();
      const map: Record<string, RunnerSnapshot> = {};
      for (const r of res.runners) map[r.id] = r;
      set({ runnersById: map, loaded: true, error: null });
      return res.runners;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Could not load runners' });
      return [];
    }
  },

  _setRunners: (next) =>
    set(() => {
      const map: Record<string, RunnerSnapshot> = {};
      for (const r of next) map[r.id] = r;
      return { runnersById: map, loaded: true, error: null };
    }),

  resetForTests: () => set({ runnersById: {}, loaded: false, error: null }),
}));

// Selector — single source of truth for "the chip's data". Returns
// `null` when the store is empty (pre-hydration) OR when the agent's
// `runnerId` doesn't match any known runner (legacy row, mid-failover
// race, etc.). The chip's hide-when-null contract keeps the header
// from showing a ghost label.
export function selectRunnerForAgentId(
  state: RunnersState,
  runnerId: string | undefined,
): RunnerSnapshot | null {
  if (!runnerId) return null;
  return state.runnersById[runnerId] ?? null;
}

// Mounts a visibility-aware refresh loop. Designed to be called once
// at the page mount (HomePage useEffect); the cleanup unbinds both the
// interval and the visibilitychange listener.
//
// `intervalMs` is exposed so tests can override; production uses the
// 30 s default.
export function startRunnersPolling(intervalMs: number = POLL_INTERVAL_MS): () => void {
  void useRunnersStore.getState().hydrate();
  const id = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      void useRunnersStore.getState().hydrate();
    }
  }, intervalMs);
  return () => window.clearInterval(id);
}
